import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import COMMON_CONSTANTS from "../constants/commonConstants";

/**
 * Validates and decodes a JWT token.
 *
 * @async
 * @param {string} token - The JWT token string to validate and decode.
 * @returns {Promise<object|boolean>} Returns the decoded token payload if valid, or false if invalid/missing.
 */
export const isValidJWT = async (token) => {
    if (!token) return false;
    try {
        const decoded = jwtDecode(token);
        return decoded;
    } catch (err) {
        return false;
    }
};

/**
 * Checks if the user session is about to expire within the next 2 minutes.
 * Used to trigger session extension prompts before the session actually expires.
 *
 * @param {object} userSessionDecoded - The decoded JWT token containing session information.
 * @param {number} userSessionDecoded.exp - The expiration timestamp of the token.
 * @returns {boolean} Returns true if session will expire within 2 minutes but hasn't expired yet, false otherwise.
 */
export const checkSessionIsAboutToExpire = (userSessionDecoded) => {
    if (!userSessionDecoded || !userSessionDecoded.exp) return false;
    const expTime = userSessionDecoded.exp;
    const now = Date.now();
    const minutesInMs = 2 * 60 * 1000; // in last 2 Minutes the session will expire and need to show extension popup
    return (expTime - minutesInMs <= now && expTime > now);
};

/**
 * Checks if the JWT token has expired by comparing the expiration time with the current time.
 *
 * @param {object} userSessionDecoded - The decoded JWT token containing session information.
 * @param {number} userSessionDecoded.exp - The expiration timestamp of the token.
 * @returns {boolean} Returns true if the token has expired or is invalid, false if still valid.
 */
export const checkIsTokenExpired = (userSessionDecoded) => {
    if (!userSessionDecoded || !userSessionDecoded.exp) return true;
    const expTime = userSessionDecoded.exp;
    const now = Date.now();
    return expTime < now;
};

/**
 * Checks if the user is authorized by verifying both the presence of a session cookie
 * and the validity of the decoded token.
 *
 * @param {object} userSessionDecoded - The decoded JWT token containing session information.
 * @returns {boolean} Returns true if user has a valid session cookie and non-expired token, false otherwise.
 */
export const checkIsAuthorized = (userSessionDecoded) => {
    try {
        const hasToken = Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME)?.length > 0;
        const isValidToken = userSessionDecoded && !checkIsTokenExpired(userSessionDecoded);
        return hasToken && isValidToken;
    } catch (err) {
        return false;
    }
};

/**
 * Checks if the current user is authorized by validating the JWT(userSession) stored in cookies.
 * First, it decodes and verifies the JWT, then checks the user's authorization status.
 *
 * @async
 * @returns {Promise<boolean>} Returns a promise that resolves to true if the user is authorized, false otherwise.
 */
export const isUserAuthorized = async () => {
    try {
        const userSessionDecoded = await isValidJWT(Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME));
        return userSessionDecoded && await checkIsAuthorized(userSessionDecoded);
    } catch (err) {
        return false;
    }
};

/**
 * Sets the session in the sessionStorage based on the name and value.
 * @param {string} name 
 * @param {string} value 
 * @returns {void}
 */
export const setSession = (name, value) => {
    if (getSession(name) !== value) {
        window.sessionStorage.setItem(name, value);
    }
};

/**
 * Gets the session from the sessionStorage based on the name.
 * @param {string} name 
 * @returns {string}
 */
export const getSession = (name) => {
    return window.sessionStorage.getItem(name);
};

/**
 * Updates the portal sessions in the sessionStorage based on the existing Cookies value.
 * @returns {void}
 */
export const updatePortalSessions = () => {
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME));
    }
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME));
    }
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME));
    }
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME));
    }
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME));
    }
    if (Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME)) {
        setSession(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME, Cookies.get(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME));
    }
}

